import { createHoverIntent } from '@js/utils/hoverIntent.js';

/**
 * Mega Menu Alpine Component
 * Handles desktop navigation with mega menu dropdowns
 *
 * Usage in Twig:
 * <div x-data="megaMenu({ megaMenus: ['products', 'services'], triggerMode: 'click' })">
 *   <button @click="toggle('products')" :class="{ 'active': isOpen('products') }">Products</button>
 *   <div x-show="isOpen('products')" x-transition>Mega menu content</div>
 * </div>
 *
 * triggerMode: 'click' (default) - Click to open/close menus
 * triggerMode: 'hover' - Hover to open menus (traditional behavior)
 *
 * hoverIntentMode: 'delay' (default) - Simple delay before switching menus
 * hoverIntentMode: 'triangle' - Amazon-style triangular safe zone detection
 */
export default function MegaMenu(config = {}) {
  return {
    activeMenu: null,
    closeTimeout: null,
    openTimeout: null,
    overrideTimeout: null,
    pendingMenuId: null,
    megaMenus: config.megaMenus || [],
    debugMenu: config.debugMenu || null,
    triggerMode: config.triggerMode || 'click', // 'click' or 'hover'
    hoverIntentMode: config.hoverIntentMode || 'triangle',
    hoverIntentDelay: config.hoverIntentDelay || 80,
    hoverIntentOverrideDelay: config.hoverIntentOverrideDelay || 160,
    hoverIntentDebug: config.hoverIntentDebug || false,
    debug: config.debug || false,
    hoverIntent: null,

    log(...args) {
      if (this.debug) console.log('[MegaMenu]', ...args);
    },

    init() {
      this.log('Initialized with config:', {
        megaMenus: this.megaMenus,
        debugMenu: this.debugMenu,
        triggerMode: this.triggerMode,
        hoverIntentMode: this.hoverIntentMode,
      });

      // Initialize hover intent if in hover mode
      if (this.isHoverMode()) {
        this.hoverIntent = createHoverIntent({
          mode: this.hoverIntentMode,
          delay: this.hoverIntentDelay,
          debug: this.hoverIntentDebug,
        });
      }

      // If debugMenu is set, auto-open that menu
      if (this.debugMenu) {
        this.activeMenu = this.debugMenu;
        this.log(`Debug mode: keeping "${this.debugMenu}" menu open`);
      }
    },

    /**
     * Check if using hover trigger mode
     * @returns {boolean}
     */
    isHoverMode() {
      return this.triggerMode === 'hover';
    },

    /**
     * Cancel any pending menu switch (hover intent)
     */
    cancelPendingOpen() {
      if (this.openTimeout) {
        this.log('Hover intent: cancelled pending switch');
        window.clearTimeout(this.openTimeout);
        this.openTimeout = null;
      }
      if (this.overrideTimeout) {
        this.log('Hover intent: cancelled override timeout');
        window.clearTimeout(this.overrideTimeout);
        this.overrideTimeout = null;
        this.pendingMenuId = null;
      }
    },

    /**
     * Start hover timer for override detection
     * Call this on mouseenter of nav items
     */
    startHoverTimer() {
      if (this.hoverIntent) {
        this.hoverIntent.startHover();
      }
    },

    /**
     * Open a specific menu
     * @param {string} menuId - The menu identifier
     * @param {boolean} immediate - Skip hover intent delay
     */
    open(menuId, immediate = false) {
      // Clear any pending close timeout
      if (this.closeTimeout) {
        window.clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      // Clear any pending open timeout
      if (this.openTimeout) {
        window.clearTimeout(this.openTimeout);
        this.openTimeout = null;
      }

      const isSwitching =
        this.activeMenu !== null && this.activeMenu !== menuId;

      // Triangle mode: block switch if moving toward current menu
      if (
        !immediate &&
        this.isHoverMode() &&
        isSwitching &&
        this.hoverIntent &&
        this.hoverIntentMode === 'triangle'
      ) {
        if (!this.hoverIntent.shouldAllowSwitch()) {
          this.log(
            `Hover intent: blocked switch to "${menuId}" (moving toward menu)`,
          );

          // Set up override timeout only if not already pending for this menu
          if (this.pendingMenuId !== menuId) {
            if (this.overrideTimeout) {
              window.clearTimeout(this.overrideTimeout);
            }
            this.pendingMenuId = menuId;
            this.log(
              `Hover intent: starting ${this.hoverIntentOverrideDelay}ms override timer for "${menuId}"`,
            );
            this.overrideTimeout = window.setTimeout(() => {
              this.log(
                `Hover intent: override - forcing switch to "${this.pendingMenuId}" after ${this.hoverIntentOverrideDelay}ms`,
              );
              this.activeMenu = this.pendingMenuId;
              this.pendingMenuId = null;
              this.overrideTimeout = null;
              this.activateHoverIntent();
            }, this.hoverIntentOverrideDelay);
          }
          return;
        }
      }

      // Clear pending state since we're proceeding with normal switch
      if (this.overrideTimeout) {
        window.clearTimeout(this.overrideTimeout);
        this.overrideTimeout = null;
      }
      this.pendingMenuId = null;

      // If switching menus in hover mode, use delay
      const needsDelay = !immediate && this.isHoverMode() && isSwitching;

      if (needsDelay) {
        this.log(
          `Hover intent: waiting ${this.hoverIntentDelay}ms before switching to "${menuId}"`,
        );
        this.openTimeout = window.setTimeout(() => {
          this.log(`Hover intent: switching to "${menuId}"`);
          this.activeMenu = menuId;
          this.activateHoverIntent();
        }, this.hoverIntentDelay);
      } else {
        this.activeMenu = menuId;
        this.activateHoverIntent();
      }

      const isMegaMenu = this.megaMenus.includes(menuId);
      this.log(
        `Open: "${menuId}" ${isMegaMenu ? '(mega menu)' : '(standard dropdown)'}${needsDelay ? ` (delayed ${this.hoverIntentDelay}ms)` : ''}`,
      );
    },

    /**
     * Activate hover intent tracking for current menu
     */
    activateHoverIntent() {
      if (!this.hoverIntent || !this.activeMenu) return;

      // Find the mega menu element and activate tracking
      this.$nextTick(() => {
        const menuEl = this.$root.querySelector(
          `[x-show="isOpen('${this.activeMenu}')"]`,
        );
        if (menuEl) {
          this.hoverIntent.activate(menuEl);
        }
      });
    },

    /**
     * Close all menus with optional delay
     * @param {number} delay - Delay in ms before closing (default: 0)
     */
    close(delay = 0) {
      // Don't close if in debug mode
      if (this.debugMenu) {
        return;
      }

      if (delay > 0) {
        this.closeTimeout = window.setTimeout(() => {
          this.activeMenu = null;
          if (this.hoverIntent) this.hoverIntent.deactivate();
        }, delay);
      } else {
        this.activeMenu = null;
        if (this.hoverIntent) this.hoverIntent.deactivate();
      }
    },

    /**
     * Check if a specific menu is open
     * @param {string} menuId - The menu identifier
     * @returns {boolean}
     */
    isOpen(menuId) {
      return this.activeMenu === menuId;
    },

    /**
     * Check if any menu is open
     * @returns {boolean}
     */
    hasOpenMenu() {
      return this.activeMenu !== null;
    },

    /**
     * Check if a mega menu (not standard dropdown) is currently open
     * @returns {boolean}
     */
    hasMegaMenuOpen() {
      return (
        this.activeMenu !== null && this.megaMenus.includes(this.activeMenu)
      );
    },

    /**
     * Toggle a menu open/closed
     * @param {string} menuId - The menu identifier
     */
    toggle(menuId) {
      if (this.isOpen(menuId)) {
        this.close();
      } else {
        this.open(menuId);
      }
    },

    /**
     * Clean up timeouts and hover intent on destroy
     */
    destroy() {
      if (this.closeTimeout) window.clearTimeout(this.closeTimeout);
      if (this.openTimeout) window.clearTimeout(this.openTimeout);
      if (this.overrideTimeout) window.clearTimeout(this.overrideTimeout);
      if (this.hoverIntent) this.hoverIntent.deactivate();
    },
  };
}
